import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import MarkdownImage from './MarkdownImage';
import 'katex/dist/katex.min.css';

const ExampleRenderer = ({ content }) => {
  if (!content) {
    console.log('ExampleRenderer: No content provided');
    return <div className="text-gray-500 italic">No example available</div>;
  }

  console.log('ExampleRenderer content:', content);

  // Custom components for markdown elements in examples
  const components = {
    // Style headings (commonly used for Problem:, Solution:, etc.)
    h1: ({ children }) => (
      <h1 className="text-lg font-bold text-blue-800 mb-3 mt-4 first:mt-0">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-base font-semibold text-blue-700 mb-2 mt-3 first:mt-0">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-base font-semibold text-blue-700 mb-2 mt-3 first:mt-0">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-sm font-semibold text-blue-600 mb-2 mt-2 first:mt-0">
        {children}
      </h4>
    ),
    // Style paragraphs
    p: ({ children }) => (
      <p className="mb-2 leading-relaxed text-gray-700">
        {children}
      </p>
    ),
    // Style lists with step-like appearance
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-3 text-gray-700 space-y-1">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-3 text-gray-700 space-y-2">
        {React.Children.map(children, (child, index) => 
          React.cloneElement(child, { index })
        )}
      </ol>
    ),
    li: ({ children, index }) => {
      // Check if this is an ordered list item with a number pattern
      const childText = React.Children.toArray(children).join('');
      const isNumberedStep = /^\d+\./.test(childText);
      
      if (isNumberedStep && typeof index === 'number') {
        return (
          <li className="flex items-start">
            <span className="inline-block w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
              {index + 1}
            </span>
            <div className="flex-1">
              {children}
            </div>
          </li>
        );
      } else {
        return (
          <li className="ml-2">
            {children}
          </li>
        );
      }
    },
    // Style strong/bold text
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-800">
        {children}
      </strong>
    ),
    // Style emphasis/italic text
    em: ({ children }) => (
      <em className="italic text-gray-700">
        {children}
      </em>
    ),
    // Style code
    code: ({ children, inline }) => (
      inline ? (
        <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      ) : (
        <code className="block bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono whitespace-pre-wrap">
          {children}
        </code>
      )
    ),
    // Style tables
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border-collapse border border-gray-300 bg-white rounded-lg shadow-sm">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-blue-50">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-gray-200">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-gray-50">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-blue-800 bg-blue-50 text-sm">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-300 px-3 py-2 text-gray-700 text-sm">
        {children}
      </td>
    ),
    // Style blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-4 py-2 mb-3 italic text-gray-700">
        {children}
      </blockquote>
    ),
    // Style horizontal rules
    hr: () => (
      <hr className="my-4 border-t border-gray-200" />
    ),
    // Style images with error handling (compact for examples)
    img: (props) => (
      <div className="my-3">
        <MarkdownImage {...props} />
      </div>
    ),
  };

  return (
    <div className="text-gray-700 prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default ExampleRenderer;
